/**
 * Micro, entreprise individuelle au réel, société à l'impôt sur les sociétés :
 * lequel des trois laisse le plus, à chiffre d'affaires et dépenses donnés.
 *
 * LA QUESTION QUE POSE LE CARROUSEL, ET QU'AUCUN OUTIL NE TRANCHAIT ICI. En
 * micro, les frais ne se déduisent pas : l'abattement forfaitaire en tient lieu
 * pour l'impôt, et les cotisations se calculent sur la totalité du chiffre
 * d'affaires. Tant que les dépenses réelles restent sous l'abattement, la micro
 * gagne. Au-delà, on paie sur un bénéfice qu'on n'a pas fait. Le point de
 * bascule ne se devine pas : il se calcule.
 *
 * TROIS CHOSES QUE LES COMPARATEURS DE RÉGIME SE PERMETTENT, ET QU'ON REFUSE
 *
 * 1. Appliquer « environ 45 % de cotisations » au réel. Les cotisations d'un
 *    indépendant sont progressives depuis le décret n° 2024-688 : la maladie
 *    part de zéro sous 20 % du plafond et n'atteint 8,5 % qu'à trois fois ce
 *    plafond, les allocations familiales ne sont dues qu'au-delà de 110 %. Le
 *    même bénéfice ne coûte pas le même pourcentage selon son montant, et un
 *    taux moyen se trompe dans les deux sens.
 *
 * 2. Oublier que l'assiette a changé au 1er janvier 2026. Ce n'est plus le
 *    revenu net : c'est une assiette unique, commune aux cotisations et à la
 *    CSG-CRDS, assise sur le revenu AVANT cotisations et abattue de 26 %.
 *
 * 3. Chiffrer une société au centime. On ne le fait pas, et on le dit : le coût
 *    d'une SASU calculé ici est un PLANCHER, parce que la cotisation accidents
 *    du travail a un taux notifié à chaque entreprise, et que la formation
 *    professionnelle et l'apprentissage ne sont pas relevés pour un président
 *    sans salarié. Une estimation qui se trompe en annonçant une société moins
 *    chère qu'elle n'est pousserait à en créer une : le sens de l'erreur compte
 *    autant que sa taille.
 *
 * L'HYPOTHÈSE DE LA SOCIÉTÉ, ASSUMÉE ET AFFICHÉE. Le dirigeant se verse tout ce
 * que l'entreprise peut lui verser en rémunération, et ne distribue pas de
 * dividendes. C'est le cas d'un solopreneur qui vit de son activité. L'arbitrage
 * rémunération-dividendes existe, il change le résultat, et c'est exactement là
 * qu'un expert-comptable gagne son prix : l'outil le dit plutôt que de le
 * simuler à l'aveugle.
 *
 * Les valeurs viennent du relevé arbitré
 * (docs/glossaries/micro-entreprise/releve-regimes-2026.md), qui donne pour
 * chacune ses deux sources officielles.
 */

import { revenuNet, activiteFiscale } from "./microEnterprise.js";
import { impotSurLeRevenu } from "./incomeTax.js";

/** Plafond annuel de la sécurité sociale 2026, arrêté du 22 décembre 2025. */
export const PASS_2026 = 48060;

/**
 * Assiette sociale unique des travailleurs indépendants, depuis le 1er janvier
 * 2026 : revenu professionnel avant déduction des cotisations, abattu de 26 %.
 * L'abattement est encadré, plancher et plafond exprimés en fraction du PASS.
 * Décret n° 2024-688, article D. 136-5 du code de la sécurité sociale.
 */
export const ABATTEMENT_ASSIETTE = 0.26;
export const ABATTEMENT_PLANCHER = 0.0176; // × PASS
export const ABATTEMENT_PLAFOND = 1.3; // × PASS

/**
 * Maladie-maternité : le taux monte par paliers jusqu'à trois fois le plafond,
 * puis le taux de base s'applique. Article D. 621-2, recopié tranche par
 * tranche : chaque palier interpole linéairement entre le taux de son début et
 * celui de sa fin, et le taux obtenu porte sur la totalité de l'assiette.
 */
export const PALIERS_MALADIE = [
    { de: 0.2, a: 0.4, depuis: 0, jusqua: 0.015 },
    { de: 0.4, a: 0.6, depuis: 0.015, jusqua: 0.04 },
    { de: 0.6, a: 1.1, depuis: 0.04, jusqua: 0.065 },
    { de: 1.1, a: 2, depuis: 0.065, jusqua: 0.077 },
    { de: 2, a: 3, depuis: 0.077, jusqua: 0.085 },
];
export const TAUX_MALADIE_BASE = 0.085; // jusqu'à 3 PASS, article D. 621-1
export const TAUX_MALADIE_HAUT = 0.065; // au-delà de 3 PASS

/** Indemnités journalières : article D. 621-3, assiette minimale de 40 % du PASS. */
export const TAUX_IJ = { commercant: 0.005, artisan: 0.005, liberal: 0.003 };
export const IJ_ASSIETTE_MINIMALE = 0.4; // × PASS
export const IJ_PLAFOND = { commercant: 5, artisan: 5, liberal: 3 }; // × PASS

/** Vieillesse de base : article D. 633-3. */
export const TAUX_VIEILLESSE_PLAFONNEE = 0.1715; // dans la limite du PASS
export const TAUX_VIEILLESSE_DEPLAFONNEE = 0.0072; // sur la totalité

/** Retraite complémentaire des artisans et commerçants : article D. 635-7. */
export const TAUX_COMPLEMENTAIRE_T1 = 0.081; // jusqu'à 1 PASS
export const TAUX_COMPLEMENTAIRE_T2 = 0.091; // de 1 à 4 PASS

/** Invalidité-décès : article D. 632-1, assiette minimale de 11,5 % du PASS. */
export const TAUX_INVALIDITE = 0.013; // dans la limite du PASS
export const INVALIDITE_ASSIETTE_MINIMALE = 0.115; // × PASS

/**
 * Contribution à la formation professionnelle d'un indépendant : un forfait
 * sur le plafond annuel, dû quel que soit le revenu, 0,25 % pour un commerçant
 * ou un libéral (120 €), 0,29 % pour un artisan (139 €) en 2026 (Urssaf, taux
 * des artisans, commerçants et libéraux ; Service-Public F23459). Jusqu'au
 * 25 septembre 2026, le réel et l'EURL l'oubliaient, alors que la micro
 * comptait la sienne : l'écart penchait toujours en leur faveur.
 */
export const TAUX_CFP_INDEPENDANT = { commercant: 0.0025, artisan: 0.0029, liberal: 0.0025 };

/** Allocations familiales : article D. 613-1, nulles sous 110 % du PASS. */
export const AF_SEUIL_BAS = 1.1; // × PASS
export const AF_SEUIL_HAUT = 1.4; // × PASS
export const TAUX_AF_PLEIN = 0.031;

/**
 * CSG-CRDS sur l'assiette unique. La part non déductible du revenu imposable
 * est de 2,9 points : 2,4 de CSG et 0,5 de CRDS.
 */
export const TAUX_CSG_CRDS = 0.097;
export const TAUX_CSG_NON_DEDUCTIBLE = 0.029;

/** Impôt sur les sociétés : article 219 du CGI. */
export const IS_TAUX_REDUIT = 0.15;
export const IS_PLAFOND_TAUX_REDUIT = 42500;
export const IS_TAUX_NORMAL = 0.25;

/**
 * Prélèvement forfaitaire unique sur les dividendes, depuis le 1er janvier
 * 2026 : 12,8 % d'impôt sur le revenu et 18,6 % de prélèvements sociaux. Il
 * était de 30 % jusqu'au 31 décembre 2025, la CSG ayant pris 1,4 point.
 */
export const PFU_IR = 0.128;
export const PFU_SOCIAL = 0.186;
export const PFU_TOTAL = PFU_IR + PFU_SOCIAL;

/**
 * Dirigeant assimilé salarié, président de SASU ou gérant non associé.
 *
 * LES COTISATIONS D'UN CADRE, MOINS LE CHÔMAGE (Service-Public F36240, vérifiée
 * le 21 février 2026). Taux de 2026, relevés le 24 septembre 2026 :
 *   - sécurité sociale : Urssaf, « Taux de cotisations, secteur privé » (mise à
 *     jour du 1er janvier 2026), et article D. 242-4 du code de la sécurité
 *     sociale (décret n° 2025-1446) pour la vieillesse ;
 *   - retraite complémentaire, CEG, CET et Apec : barème Agirc-Arrco 2026 et sa
 *     circulaire 2025-16 SG-DRJ ; tranches 1 et 2 recoupées sur F15396.
 *
 * LA PART SALARIALE N'EST PAS DANS LA PART PATRONALE. Jusqu'au 24 septembre
 * 2026, la vieillesse patronale valait ici 15,45 % et 2,51 % : c'était le
 * total des deux parts, et la part salariale se comptait une seconde fois plus
 * bas. Le coût d'une SASU sortait trop haut de plus de 7 points sur le brut.
 *
 * AUCUN TAUX RÉDUIT. La réduction générale dégressive unique, qui absorbe
 * depuis 2026 les taux réduits de maladie et d'allocations familiales, suppose
 * un contrat de travail et l'assurance chômage (Urssaf) : un président de SASU
 * n'a ni l'un ni l'autre.
 *
 * RECOUPÉ LE 24 SEPTEMBRE 2026 AVEC LE MOTEUR DE L'URSSAF (modele-social
 * 11.1.0, celui de mon-entreprise.urssaf.fr), ligne par ligne : maladie,
 * vieillesse, allocations familiales, Agirc-Arrco, CEG, CSA et Fnal tombent au
 * centime. Il applique au président la contribution à la formation
 * professionnelle (0,55 % sous onze salariés) et la taxe d'apprentissage
 * (0,59 % et 0,68 % avec le solde), aux taux de la page Urssaf du secteur
 * privé : elles sont comptées. Il ne lui applique ni l'Apec ni la contribution
 * au dialogue social : elles ne le sont plus.
 *
 * CE QUI MANQUE ENCORE, et fait du coût un plancher : la cotisation accidents
 * du travail, dont le taux est notifié à chaque entreprise par la Carsat, et la
 * prévoyance ou la mutuelle, que le président n'est pas tenu de souscrire pour
 * lui-même. `plancher: true` le rappelle à qui lit le résultat.
 */
export const ASSIMILE_SALARIE = {
    patronal: {
        maladie: 0.13, // taux plein, sur tout le brut
        csa: 0.003, // contribution solidarité autonomie
        vieillessePlafonnee: 0.0855,
        vieillesseDeplafonnee: 0.0211,
        allocationsFamiliales: 0.0525,
        fnal: 0.001, // moins de 50 salariés, dans la limite du plafond
        formationProfessionnelle: 0.0055, // moins de onze salariés
        taxeApprentissage: 0.0068, // part principale 0,59 % et solde 0,09 %
        retraiteT1: 0.0472,
        retraiteT2: 0.1295,
        cegT1: 0.0129,
        cegT2: 0.0162,
        cet: 0.0021, // sur T1 et T2, dû seulement au-delà du plafond
    },
    salarial: {
        vieillessePlafonnee: 0.069,
        vieillesseDeplafonnee: 0.004,
        retraiteT1: 0.0315,
        retraiteT2: 0.0864,
        cegT1: 0.0086,
        cegT2: 0.0108,
        cet: 0.0014,
    },
    // CSG-CRDS sur 98,25 % du brut dans la limite de 4 plafonds, sur la
    // totalité au-delà ; 2,4 points de CSG et la CRDS ne sont pas déductibles.
    csgCrds: { taux: 0.097, assiette: 0.9825, limiteAbattement: 4, nonDeductible: 0.029 },
    retraiteT2Plafond: 8, // × PASS
};

/**
 * Abattement de 10 % pour frais professionnels sur les rémunérations, dans
 * ses limites : 509 € au moins, 14 555 € au plus par personne, revenus de 2025
 * (Service-Public F1989, relevée le 25 septembre 2026), et jamais plus que la
 * rémunération elle-même. La rémunération d'un gérant (article 62 du CGI) y a
 * droit comme un salaire.
 *
 * SANS SON PLAFOND, L'IMPÔT DES HAUTES RÉMUNÉRATIONS SORTAIT TROP BAS. Jusqu'au
 * 25 septembre 2026, les moteurs retiraient 10 % sans borne : au-delà de
 * 145 550 € de rémunération, l'impôt était sous-estimé : de 1 787 € pour une
 * EURL libérale à 250 000 € de chiffre d'affaires, de 7 536 € à 400 000 €, et
 * un verdict pouvait basculer.
 * Les deux bornes se revoient chaque année avec le barème.
 */
export const ABATTEMENT_FRAIS_PROFESSIONNELS = 0.1;
export const ABATTEMENT_FRAIS_MINIMUM = 509;
export const ABATTEMENT_FRAIS_MAXIMUM = 14555;

/** L'abattement pour frais professionnels d'une rémunération imposable. */
export function abattementFraisProfessionnels(remuneration) {
    if (remuneration <= 0) return 0;
    const dix = Math.min(remuneration * ABATTEMENT_FRAIS_PROFESSIONNELS, ABATTEMENT_FRAIS_MAXIMUM);
    return Math.min(remuneration, Math.max(ABATTEMENT_FRAIS_MINIMUM, dix));
}

/** Une rémunération après l'abattement pour frais professionnels. */
export function apresFraisProfessionnels(remuneration) {
    return Math.max(0, remuneration - abattementFraisProfessionnels(remuneration));
}

const positif = (x) => Math.max(0, x);

/**
 * L'assiette unique, à partir du revenu professionnel avant cotisations.
 *
 * L'abattement de 26 % est encadré : il ne peut être inférieur à 1,76 % du
 * plafond ni supérieur à 130 %. Sur un revenu nul ou négatif, il n'y a rien à
 * abattre et l'assiette est nulle : appliquer le plancher donnerait une assiette
 * négative, qu'aucune cotisation ne sait lire.
 */
export function assietteUnique(revenuAvantCotisations, pass = PASS_2026) {
    if (revenuAvantCotisations <= 0) return 0;
    const brut = revenuAvantCotisations * ABATTEMENT_ASSIETTE;
    const abattement = Math.min(
        Math.max(brut, ABATTEMENT_PLANCHER * pass),
        ABATTEMENT_PLAFOND * pass,
    );
    return positif(revenuAvantCotisations - abattement);
}

/** Taux de maladie-maternité applicable à une assiette, article D. 621-2. */
export function tauxMaladie(assiette, pass = PASS_2026) {
    if (assiette <= 0) return 0;
    const r = assiette / pass;
    if (r < 0.2) return 0;
    if (r >= 3) return TAUX_MALADIE_BASE;
    const palier = PALIERS_MALADIE.find((p) => r >= p.de && r < p.a);
    if (!palier) return TAUX_MALADIE_BASE;
    const part = (r - palier.de) / (palier.a - palier.de);
    return palier.depuis + (palier.jusqua - palier.depuis) * part;
}

/** Cotisation de maladie-maternité, taux de base au-delà de trois plafonds. */
export function cotisationMaladie(assiette, pass = PASS_2026) {
    if (assiette <= 0) return 0;
    if (assiette <= 3 * pass) return assiette * tauxMaladie(assiette, pass);
    return 3 * pass * TAUX_MALADIE_BASE + (assiette - 3 * pass) * TAUX_MALADIE_HAUT;
}

/** Taux d'allocations familiales, article D. 613-1. */
export function tauxAllocationsFamiliales(assiette, pass = PASS_2026) {
    const r = assiette / pass;
    if (r <= AF_SEUIL_BAS) return 0;
    if (r >= AF_SEUIL_HAUT) return TAUX_AF_PLEIN;
    return (TAUX_AF_PLEIN / (AF_SEUIL_HAUT - AF_SEUIL_BAS)) * (r - AF_SEUIL_BAS);
}

/** La famille de cotisation dont relève l'activité, pour les IJ. */
export function familleTns(activite, artisan = false) {
    if (activite === "bnc" || activite === "cipav") return "liberal";
    return artisan ? "artisan" : "commercant";
}

/**
 * Toutes les cotisations d'un travailleur indépendant au réel, ligne par ligne.
 *
 * Les assiettes minimales des indemnités journalières et de l'invalidité-décès
 * sont appliquées : elles font qu'un revenu très faible paie quand même
 * quelques dizaines d'euros, ce qui change le verdict aux petits montants.
 * L'assiette minimale de la vieillesse de base (450 SMIC horaire) n'est PAS
 * appliquée : c'est écrit dans `approximations`.
 */
export function cotisationsIndependant({
    revenuAvantCotisations,
    activite = "services",
    artisan = false,
    pass = PASS_2026,
}) {
    const assiette = assietteUnique(revenuAvantCotisations, pass);
    const famille = familleTns(activite, artisan);
    const plafonnee = Math.min(assiette, pass);

    const maladie = cotisationMaladie(assiette, pass);
    const ij =
        Math.min(
            Math.max(assiette, IJ_ASSIETTE_MINIMALE * pass),
            IJ_PLAFOND[famille] * pass,
        ) * TAUX_IJ[famille];
    const vieillesseBase =
        plafonnee * TAUX_VIEILLESSE_PLAFONNEE + assiette * TAUX_VIEILLESSE_DEPLAFONNEE;
    const complementaire =
        plafonnee * TAUX_COMPLEMENTAIRE_T1 +
        positif(Math.min(assiette, 4 * pass) - pass) * TAUX_COMPLEMENTAIRE_T2;
    const invalidite =
        Math.min(Math.max(assiette, INVALIDITE_ASSIETTE_MINIMALE * pass), pass) *
        TAUX_INVALIDITE;
    const allocations = assiette * tauxAllocationsFamiliales(assiette, pass);
    const csgCrds = assiette * TAUX_CSG_CRDS;
    const formation = pass * TAUX_CFP_INDEPENDANT[famille];

    const total =
        maladie + ij + vieillesseBase + complementaire + invalidite + allocations + csgCrds + formation;

    return {
        assiette,
        maladie,
        ij,
        vieillesseBase,
        complementaire,
        invalidite,
        allocations,
        csgCrds,
        formation,
        total,
        // La CSG non déductible et la CRDS ne s'imputent pas sur le revenu
        // imposable : les oublier fait sous-estimer l'impôt.
        nonDeductible: assiette * TAUX_CSG_NON_DEDUCTIBLE,
    };
}

/** Impôt sur les sociétés sur un résultat, article 219 du CGI. */
export function impotSocietes(resultat) {
    if (resultat <= 0) return 0;
    const reduit = Math.min(resultat, IS_PLAFOND_TAUX_REDUIT);
    return reduit * IS_TAUX_REDUIT + positif(resultat - IS_PLAFOND_TAUX_REDUIT) * IS_TAUX_NORMAL;
}

/** Cotisations d'un dirigeant assimilé salarié, parts patronale et salariale. */
export function cotisationsAssimileSalarie(brut, pass = PASS_2026) {
    if (brut <= 0) return { patronales: 0, salariales: 0, nonDeductible: 0, coutEmployeur: 0, net: 0, retraite: 0 };
    const { patronal, salarial, csgCrds, retraiteT2Plafond } = ASSIMILE_SALARIE;
    const plafonne = Math.min(brut, pass);
    const tranche2 = positif(Math.min(brut, retraiteT2Plafond * pass) - pass);
    // La CET ne s'appelle que si le brut dépasse la tranche 1, mais porte
    // alors sur les deux tranches.
    const assietteCet = brut > pass ? plafonne + tranche2 : 0;

    const patronales =
        brut * (patronal.maladie + patronal.csa) +
        plafonne * patronal.vieillessePlafonnee +
        brut * patronal.vieillesseDeplafonnee +
        brut * patronal.allocationsFamiliales +
        plafonne * patronal.fnal +
        brut * (patronal.formationProfessionnelle + patronal.taxeApprentissage) +
        plafonne * (patronal.retraiteT1 + patronal.cegT1) +
        tranche2 * (patronal.retraiteT2 + patronal.cegT2) +
        assietteCet * patronal.cet;

    const limite = csgCrds.limiteAbattement * pass;
    const assietteCsg =
        Math.min(brut, limite) * csgCrds.assiette + positif(brut - limite);
    const salariales =
        plafonne * salarial.vieillessePlafonnee +
        brut * salarial.vieillesseDeplafonnee +
        plafonne * (salarial.retraiteT1 + salarial.cegT1) +
        tranche2 * (salarial.retraiteT2 + salarial.cegT2) +
        assietteCet * salarial.cet +
        assietteCsg * csgCrds.taux;

    // Ce qui part aux caisses de retraite, parts patronale et salariale :
    // vieillesse de base, Agirc-Arrco et ses contributions d'équilibre.
    const retraite =
        plafonne * (patronal.vieillessePlafonnee + salarial.vieillessePlafonnee) +
        brut * (patronal.vieillesseDeplafonnee + salarial.vieillesseDeplafonnee) +
        plafonne * (patronal.retraiteT1 + salarial.retraiteT1 + patronal.cegT1 + salarial.cegT1) +
        tranche2 * (patronal.retraiteT2 + salarial.retraiteT2 + patronal.cegT2 + salarial.cegT2) +
        assietteCet * (patronal.cet + salarial.cet);

    return {
        patronales,
        salariales,
        nonDeductible: assietteCsg * csgCrds.nonDeductible,
        coutEmployeur: brut + patronales,
        net: brut - salariales,
        retraite,
    };
}

/**
 * La rémunération brute qu'une enveloppe peut payer, cotisations comprises.
 *
 * Dichotomie plutôt que formule : les barèmes sont progressifs et par tranches,
 * donc le coût n'est pas proportionnel au brut. La recherche tient parce que le
 * coût croît avec le brut, ce qu'aucun barème ne dément.
 */
export function remunerationPour(enveloppe, cout) {
    if (enveloppe <= 0) return 0;
    let bas = 0;
    let haut = enveloppe;
    for (let i = 0; i < 60; i++) {
        const milieu = (bas + haut) / 2;
        if (cout(milieu) > enveloppe) haut = milieu;
        else bas = milieu;
    }
    return bas;
}

/**
 * Le coût fiscal marginal d'un revenu, c'est-à-dire ce que le foyer paie en
 * plus à cause de lui. Sans cela, un conjoint bien payé ferait apparaître tous
 * les régimes comme ruineux, et leur écart serait illisible.
 */
function impotMarginal(revenuImposable, { autresRevenus = 0, ...foyer }) {
    const avec = impotSurLeRevenu(positif(autresRevenus + revenuImposable), foyer);
    const sans = impotSurLeRevenu(positif(autresRevenus), foyer);
    return positif(avec - sans);
}

/** Ce que la micro laisse, dépenses réelles payées de sa poche. */
export function scenarioMicro({ chiffreAffaires, charges, activite, artisan, foyer }) {
    const detail = revenuNet({ chiffreAffaires, activite, artisan, ...foyer });
    return {
        regime: "micro",
        chiffreAffaires,
        charges,
        cotisations: detail.cotisations + detail.cfp,
        impot: detail.impot,
        // L'abattement forfaitaire tient lieu de frais : les dépenses réelles
        // sortent de la poche APRÈS impôt et cotisations, et ne réduisent rien.
        net: detail.net - charges,
        abattementForfaitaire: chiffreAffaires - detail.benefice,
    };
}

/** Ce que l'entreprise individuelle au réel laisse. */
export function scenarioReel({ chiffreAffaires, charges, activite, artisan, foyer }) {
    const benefice = positif(chiffreAffaires - charges);
    const cotisations = cotisationsIndependant({
        revenuAvantCotisations: benefice,
        activite,
        artisan,
    });
    const imposable = positif(benefice - cotisations.total + cotisations.nonDeductible);
    const impot = impotMarginal(imposable, foyer);
    return {
        regime: "reel",
        chiffreAffaires,
        charges,
        benefice,
        cotisations: cotisations.total,
        detailCotisations: cotisations,
        impot,
        net: benefice - cotisations.total - impot,
    };
}

/**
 * Ce qu'une EURL à l'IS laisse, gérant travailleur indépendant.
 *
 * LE PIÈGE QUE CE SCÉNARIO A FAILLI TOMBER DEDANS. On serait tenté de chercher
 * par dichotomie la rémunération R telle que R plus ses cotisations épuise
 * l'enveloppe, puis de faire cotiser R. C'est faux : l'assiette d'un gérant
 * travailleur indépendant est son revenu AVANT déduction des cotisations,
 * exactement comme celle d'un entrepreneur individuel au réel. La société les
 * prend en charge et les déduit, mais elles restent assises sur l'enveloppe
 * entière. Cotiser sur la rémunération nette de cotisations faisait apparaître
 * l'EURL quatre mille euros au-dessus du réel, un écart qui n'existe pas.
 *
 * Ce qui reste vrai, et qui fait toute la différence entre les deux : la
 * rémunération d'un gérant relève de l'article 62 du CGI, donc de l'abattement
 * de 10 % pour frais professionnels, que le bénéfice d'une entreprise
 * individuelle n'a pas. Tout versé en rémunération, l'EURL ne laisse rien à
 * l'impôt sur les sociétés : c'est l'arbitrage avec les dividendes qui le
 * ferait, et il n'est pas simulé ici.
 */
export function scenarioEurl({ chiffreAffaires, charges, activite, artisan, foyer }) {
    const disponible = positif(chiffreAffaires - charges);
    const cotisations = cotisationsIndependant({
        revenuAvantCotisations: disponible,
        activite,
        artisan,
    });
    const remuneration = positif(disponible - cotisations.total);
    const resultat = 0;
    const imposable = positif(
        apresFraisProfessionnels(remuneration + cotisations.nonDeductible),
    );
    const impot = impotMarginal(imposable, foyer);
    return {
        regime: "eurl",
        chiffreAffaires,
        charges,
        remuneration,
        cotisations: cotisations.total,
        detailCotisations: cotisations,
        resultatSociete: resultat,
        impotSocietes: impotSocietes(resultat),
        impot,
        net: remuneration - impot,
    };
}

/** Ce qu'une SASU à l'IS laisse, président assimilé salarié. */
export function scenarioSasu({ chiffreAffaires, charges, foyer }) {
    const disponible = positif(chiffreAffaires - charges);
    const brut = remunerationPour(disponible, (r) => cotisationsAssimileSalarie(r).coutEmployeur);
    const c = cotisationsAssimileSalarie(brut);
    const resultat = positif(disponible - c.coutEmployeur);
    const is = impotSocietes(resultat);
    const imposable = apresFraisProfessionnels(c.net + c.nonDeductible);
    const impot = impotMarginal(imposable, foyer);
    return {
        regime: "sasu",
        chiffreAffaires,
        charges,
        remuneration: brut,
        cotisations: c.patronales + c.salariales,
        detailCotisations: c,
        resultatSociete: resultat,
        impotSocietes: is,
        impot,
        net: c.net - impot,
        // Les accidents du travail manquent : le coût est un plancher, donc le
        // net affiché est un plafond.
        plancher: true,
    };
}

/**
 * Écart sous lequel on refuse de désigner un gagnant.
 *
 * Mille euros par an sur un revenu de solopreneur, c'est moins que ce que coûte
 * la comptabilité d'une société, et moins que l'imprécision assumée du calcul.
 * Annoncer un vainqueur là-dedans serait donner une fausse précision à une
 * décision qui se prend sur d'autres critères.
 */
export const ECART_NEGLIGEABLE = 1000;

/**
 * Le comparatif complet, et son verdict quand il y en a un.
 *
 * `activite` prend les valeurs de la micro-entreprise : services, bnc, cipav ou
 * vente. La vente n'est comparée que pour mémoire : ses dépenses sont surtout
 * des achats de marchandises, et l'abattement de 71 % rend le réel gagnant bien
 * plus tard.
 */
export function comparerRegimes({
    chiffreAffaires,
    charges = 0,
    activite = "services",
    artisan = false,
    enCouple = false,
    enfants = 0,
    parentIsole = false,
    autresRevenus = 0,
}) {
    const foyer = { enCouple, enfants, parentIsole, autresRevenus };
    const commun = { chiffreAffaires, charges, activite, artisan, foyer };

    const scenarios = [
        scenarioMicro(commun),
        scenarioReel(commun),
        scenarioEurl(commun),
        scenarioSasu({ chiffreAffaires, charges, foyer }),
    ];

    const classes = [...scenarios].sort((a, b) => b.net - a.net);
    const premier = classes[0];
    const second = classes[1];
    const ecart = premier.net - second.net;
    const micro = scenarios[0];

    // LE LECTEUR PART DE LA MICRO, ET C'EST ELLE QU'IL VEUT SITUER. Un verdict
    // qui ne compare que les deux premiers finit par annoncer « trop serré »
    // alors que la micro est mille lieues derrière : l'EURL et le réel se
    // tiennent presque toujours à l'abattement de 10 % près. Le gain de
    // changement, lui, répond à la question posée.
    const gainSiChangement = premier.net - micro.net;

    return {
        scenarios,
        classes,
        gagnant: ecart < ECART_NEGLIGEABLE ? null : premier.regime,
        exAequo: ecart < ECART_NEGLIGEABLE ? [premier.regime, second.regime] : null,
        ecart,
        // Ce que quitter la micro rapporterait, et pour aller où.
        microEnTete: micro.regime === premier.regime,
        gainSiChangement,
        changementUtile: gainSiChangement >= ECART_NEGLIGEABLE,
        meilleurRegime: premier.regime,
        // Le seul chiffre que le carrousel promet : le moment où les dépenses
        // réelles dépassent l'abattement forfaitaire.
        abattementForfaitaire: scenarios[0].abattementForfaitaire,
        chargesDepassentAbattement: charges > scenarios[0].abattementForfaitaire,
        approximations: [
            "les taxes pour frais de chambre consulaire",
            "l'assiette minimale de la vieillesse de base, 450 fois le SMIC horaire, qui n'est pas appliquée",
            "en SASU, la cotisation accidents du travail, dont le taux est notifié à chaque entreprise",
            "le coût d'un expert-comptable, qu'aucune source officielle ne publie",
        ],
    };
}

/**
 * Le chiffre de dépenses à partir duquel le réel passe devant la micro, à
 * chiffre d'affaires donné. Rend `null` quand le réel ne passe jamais devant
 * dans la plage testée, ce qui arrive en vente au taux d'abattement de 71 %.
 */
export function bascule({
    chiffreAffaires,
    activite = "services",
    artisan = false,
    enCouple = false,
    enfants = 0,
    parentIsole = false,
    autresRevenus = 0,
}) {
    const base = { chiffreAffaires, activite, artisan, enCouple, enfants, parentIsole, autresRevenus };
    const gagne = (charges) => {
        const r = comparerRegimes({ ...base, charges });
        const micro = r.scenarios.find((s) => s.regime === "micro");
        const reel = r.scenarios.find((s) => s.regime === "reel");
        return reel.net > micro.net;
    };
    if (!gagne(chiffreAffaires)) return null;
    if (gagne(0)) return 0;

    let bas = 0;
    let haut = chiffreAffaires;
    for (let i = 0; i < 40; i++) {
        const milieu = (bas + haut) / 2;
        if (gagne(milieu)) haut = milieu;
        else bas = milieu;
    }
    return Math.round(haut);
}

/** Les régimes que cet outil sait comparer, dans l'ordre où ils s'affichent. */
export const REGIMES = ["micro", "reel", "eurl", "sasu"];

/** Le régime fiscal de la micro sert de repère pour l'abattement affiché. */
export { activiteFiscale };
